import { useMemo, useRef, useState } from "react";
import Avatar from "../components/Avatar";
import BuzzerArea from "../components/BuzzerArea";
import Button from "../components/Button";
import Countdown from "../components/Countdown";
import DuBistDran from "../components/DuBistDran";
import HistoryModal from "../components/HistoryModal";
import Icon from "../components/icons/Icon";
import JahrUebersicht from "../components/JahrUebersicht";
import Leiste from "../components/Leiste";
import Podium from "../components/Podium";
import PunktePille from "../components/PunktePille";
import PunkteSheet from "../components/PunkteSheet";
import SongLoesung from "../components/SongLoesung";
import SpielKopf from "../components/SpielKopf";
import SpielMenue from "../components/SpielMenue";
import StatusPille, { WartePunkte } from "../components/StatusPille";
import TaskHeading from "../components/TaskHeading";
import Vinyl from "../components/Vinyl";
import { AUFGABEN } from "../aufgaben";
import { farbNummern } from "../spieler";
import "./PlayerGameScreen.css";

const KNAPP_AB = 5;

/** Wie viele verschiedene Runden stehen im Verlauf? */
const rundenImVerlauf = (verlauf) => new Set(verlauf.map((e) => e.roundNumber)).size;

// Mitspieler-Ansicht des laufenden Spiels: Kopf · Mitte · Daumenzone.
// Keine Einstellungen, keine Punktevergabe — nur raten, buzzern, schauen.
export default function PlayerGameScreen({
  room,
  playerTimer,
  jahrErgebnis,
  jahrTipp,
  setJahrTipp,
  showHistoryModal,
  setShowHistoryModal,
  showLeaveConfirm,
  setShowLeaveConfirm,
  onVerlassen,
}) {
  const [zeigePunkte, setZeigePunkte] = useState(false);
  const r = room.room || {};
  const game = r.game || {};
  const spieler = useMemo(() => r.players || [], [r.players]);
  const history = r.history || [];
  const myId = room.myPlayerId;
  const ich = spieler.find((p) => p.id === myId) || null;
  const farbe = useMemo(() => farbNummern(spieler.map((p) => p.name)), [spieler]);
  const eintraege = spieler.map((p) => ({ key: p.id, name: p.name, score: p.score }));

  // Gesamtdauer fuer den Ring. Der Raum schickt sie mit (timerTotalMs);
  // bei einem alten Worker merken wir uns die groesste Restzeit der Runde.
  const groessteRef = useRef({ runde: null, sek: 0 });
  if (groessteRef.current.runde !== game.roundCount) groessteRef.current = { runde: game.roundCount, sek: 0 };
  if (playerTimer !== null && playerTimer > groessteRef.current.sek) groessteRef.current.sek = playerTimer;
  const gesamt = game.timerTotalMs ? Math.ceil(game.timerTotalMs / 1000) : groessteRef.current.sek;
  const rest = playerTimer === null || !gesamt ? null : playerTimer / gesamt;
  const knapp = playerTimer !== null && playerTimer <= KNAPP_AB && game.phase === "PLAYING";

  const lastSolution = r.lastSolution || null;
  const iHaveFloor = r.buzz && r.buzz.playerId === myId;
  const naechsteAngesagt = game.phase === "TASK" || game.phase === "READY";

  const menue = showLeaveConfirm && (
    <SpielMenue
      onClose={() => setShowLeaveConfirm(false)}
      eintraege={[
        {
          icon: "tuer",
          label: "Raum verlassen",
          gefahr: true,
          frage: "Du verlässt das Spiel. Deine Punkte bleiben im Raum.",
          bestaetigen: "Verlassen",
          onClick: onVerlassen,
        },
      ]}
    />
  );
  const verlauf = showHistoryModal && (
    <HistoryModal
      eintraege={history}
      namen={spieler.map((p) => p.name)}
      leerText="Noch keine Punkte vergeben."
      onClose={() => setShowHistoryModal(false)}
    />
  );
  const punkte = zeigePunkte && (
    <PunkteSheet eintraege={eintraege} ich={ich?.name ?? null} farbe={farbe} onClose={() => setZeigePunkte(false)} />
  );

  // --- Spielende ---
  if (game.phase === "FINISHED") {
    const runden = rundenImVerlauf(history);
    return (
      <div className="spiel stimmung-gruen">
        <SpielKopf
          label="Spiel beendet"
          wert={runden === 1 ? "1 Runde" : `${runden} Runden`}
          onMenue={() => setShowLeaveConfirm(true)}
          menueIcon="x"
          menueLabel="Raum verlassen"
        />
        <main className="spiel-mitte">
          <Podium eintraege={eintraege} ich={ich?.name ?? null} farbe={farbe} alsListe />
        </main>
        <div className="daumenzone">
          <Button variant="neben" onClick={() => setShowHistoryModal(true)}>
            <Icon name="verlauf" />
            Verlauf ansehen
          </Button>
          <p className="hinweis-text">Der Host entscheidet, ob es weitergeht.</p>
        </div>
        {verlauf}
        {menue}
      </div>
    );
  }

  const stimmung = !lastSolution && r.buzzArmed && !r.buzz && !(r.lockedOut || []).includes(myId) ? " stimmung-rot" : "";

  return (
    <div className={`spiel${stimmung}`}>
      <SpielKopf
        wert={game.roundCount || 1}
        pille={<PunktePille eintraege={eintraege} ich={ich?.name ?? null} farbe={farbe} onClick={() => setZeigePunkte(true)} />}
        onVerlauf={() => setShowHistoryModal(true)}
        verlaufAnzahl={history.length}
        onMenue={() => setShowLeaveConfirm(true)}
      />

      {/* Ohne Host laeuft nichts: er steuert Spotify und deckt die Loesung
          auf. Das muss auf den Handys sichtbar sein, sonst druecken alle
          ratlos auf einen toten Buzzer. */}
      {room.status === "connected" && room.room && !room.room.hostConnected && (
        <Leiste icon="pause" titel="Der Host ist gerade weg" text="Das Spiel wartet, bis er zurück ist." />
      )}
      {room.status !== "connected" &&
        (room.status === "gone" ? (
          <Leiste farbe="rot" icon="wlan-aus" titel="Der Raum ist abgelaufen" text="Der Host kann einen neuen öffnen." />
        ) : (
          <Leiste farbe="rot" icon="wlan-aus" titel="Verbindung unterbrochen" text="Versuche neu …" dreht />
        ))}

      {lastSolution ? (
        /* Runde entschieden: die Loesung steht bei allen, bis der naechste
           Song laeuft. Ist die naechste Aufgabe schon angesagt, steht sie
           klein darueber, damit sich alle einstellen koennen. */
        <>
          <main className="spiel-mitte">
            {naechsteAngesagt && (
              <span className="ueberzeile gruen">
                Als Nächstes: {AUFGABEN[game.taskType]?.name || "…"}
              </span>
            )}
            {jahrErgebnis ? (
              <>
                <JahrUebersicht ergebnis={jahrErgebnis} meineId={myId} farbe={farbe} spieler={spieler} />
                <div className="loesung-karte">
                  <SongLoesung songData={lastSolution} />
                </div>
              </>
            ) : (
              <>
                <span className="chip">
                  {lastSolution.by ? (
                    <>
                      <Avatar name={lastSolution.by} farbe={farbe(lastSolution.by)} />
                      {lastSolution.by} hatte recht
                      <span className="plus">+1</span>
                    </>
                  ) : (
                    <>Keiner wusste es</>
                  )}
                </span>
                <SongLoesung songData={lastSolution} gross />
              </>
            )}
          </main>
          <div className="daumenzone">
            <div className="warteflaeche" role="status">
              Gleich geht's weiter <WartePunkte />
            </div>
          </div>
        </>
      ) : (
        <>
          <main className="spiel-mitte">
            <TaskHeading
              typ={game.taskType}
              ueberzeile={naechsteAngesagt ? `Runde ${game.roundCount || 1} · Gleich geht's los` : "Gesucht wird"}
            />
            <Vinyl
              playing={game.phase === "PLAYING"}
              rest={rest}
              dauer={gesamt || null}
              knapp={knapp}
              groesse={game.taskType === "YEAR" ? "klein" : "mittel"}
            />
            <div className="deck-zeile">
              <Countdown sekunden={playerTimer} knapp={knapp} />
              {game.phase === "SOLVED" && <StatusPille laeuft={false} />}
            </div>
          </main>
          <div className="daumenzone">
            <BuzzerArea room={room} jahrTipp={jahrTipp} setJahrTipp={setJahrTipp} farbe={farbe} />
          </div>
        </>
      )}

      {iHaveFloor && (
        <DuBistDran
          name={ich?.name || r.buzz.name}
          farbe={farbe(ich?.name || r.buzz.name)}
          taskType={game.taskType}
          loesung={room.revealedSolution}
          onAufdecken={room.requestSolution}
          onRichtig={() => room.reportAnswer(true)}
          onFalsch={() => room.reportAnswer(false)}
        />
      )}

      {verlauf}
      {punkte}
      {menue}
    </div>
  );
}
